import type { ApiErrorBody } from '@shared/api';
import { ROUTES } from '@shared/api';

/**
 * Typed fetch wrapper for the school fees API.
 *
 * - Always sends cookies (`credentials: 'same-origin'`).
 * - Attaches `X-CSRF-Token` from the auth store on every mutating request.
 * - Generates an `Idempotency-Key` for payment POSTs and mirrors it into the
 *   JSON body field `idempotency_key` so retries can never double-post money.
 * - Parses `ApiErrorBody` responses into a thrown `ApiError`.
 * - Never logs tokens or session material.
 */

export type ApiErrorCode = ApiErrorBody['error']['code'] | 'network_error' | 'unexpected_error';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: ApiErrorBody['error']['details'];
  readonly correlationId: string | undefined;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCode,
    details?: ApiErrorBody['error']['details'],
    correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.correlationId = correlationId;
  }

  /** Field-level validation messages keyed by path, when the server provided them. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      if (!(detail.path in out)) out[detail.path] = detail.message;
    }
    return out;
  }
}

/* ------------------------------------------------------------------ */
/* Auth store bridge: the auth provider pushes the CSRF token here.    */
/* ------------------------------------------------------------------ */

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register a callback fired whenever the API returns 401 on a protected call. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/* ------------------------------------------------------------------ */
/* Request plumbing                                                    */
/* ------------------------------------------------------------------ */

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  /** Extra headers merged into the request. */
  headers?: Record<string, string>;
  /** Suppress the global 401 handler (used by the session probe and login). */
  silent401?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, params?: QueryParams): string {
  if (!params) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  if (query === '') return path;
  return path.includes('?') ? `${path}&${query}` : `${path}?${query}`;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older engines: time + random, still globally unique enough.
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function isPaymentPost(path: string): boolean {
  return path === ROUTES.payments || path.startsWith(`${ROUTES.payments}/`);
}

function isPlainObjectBody(body: unknown): body is Record<string, unknown> {
  return typeof body === 'object' && body !== null && !Array.isArray(body) && !(body instanceof FormData);
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}`;
  let code: ApiErrorCode = 'unexpected_error';
  let details: ApiErrorBody['error']['details'];
  let correlationId: string | undefined;

  const text = await response.text().catch(() => '');
  if (text) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'error' in parsed &&
        typeof (parsed as ApiErrorBody).error === 'object' &&
        (parsed as ApiErrorBody).error !== null
      ) {
        const body = parsed as ApiErrorBody;
        message = body.error.message || message;
        code = body.error.code ?? code;
        details = body.error.details;
        correlationId = body.error.correlation_id;
      } else {
        message = text.slice(0, 300);
      }
    } catch {
      message = response.statusText || message;
    }
  }

  return new ApiError(message, response.status, code, details, correlationId);
}

async function coreFetch(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  init: { body?: BodyInit | null; params?: QueryParams; options?: RequestOptions; mutating?: boolean; idempotentPayment?: boolean },
): Promise<Response> {
  const headers = new Headers(init.options?.headers);

  if (init.mutating) {
    if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
    if (init.idempotentPayment) {
      headers.set('Idempotency-Key', generateIdempotencyKey());
    }
  }

  const response = await fetch(buildUrl(path, init.params), {
    method,
    credentials: 'same-origin',
    headers,
    body: init.body ?? null,
    signal: init.options?.signal,
  }).catch((error: unknown) => {
    throw new ApiError(
      error instanceof Error && error.name === 'AbortError'
        ? 'The request was cancelled'
        : 'Network error — check your connection and try again',
      0,
      'network_error',
    );
  });

  if (!response.ok) {
    if (response.status === 401 && !init.options?.silent401 && unauthorizedHandler) {
      unauthorizedHandler();
    }
    throw await parseError(response);
  }

  return response;
}

async function readJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('The server returned an unexpected non-JSON response', response.status, 'unexpected_error');
  }
}

async function requestJson<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  params?: QueryParams,
  options?: RequestOptions,
): Promise<T> {
  const mutating = method !== 'GET';
  const idempotentPayment = method === 'POST' && isPaymentPost(path);

  // Payment POSTs must use the SAME key in the header and the JSON body:
  // reuse a caller-provided body key when present, otherwise generate one and
  // mirror it into the body.
  const existingKey =
    idempotentPayment && isPlainObjectBody(body) && typeof body.idempotency_key === 'string' && body.idempotency_key.length > 0
      ? body.idempotency_key
      : null;
  const paymentKey = idempotentPayment ? (existingKey ?? generateIdempotencyKey()) : null;

  let payload = body;
  if (paymentKey !== null && existingKey === null && isPlainObjectBody(body)) {
    payload = { ...body, idempotency_key: paymentKey };
  }

  const headers: Record<string, string> = { ...(options?.headers ?? {}) };
  if (paymentKey !== null) headers['Idempotency-Key'] = paymentKey;

  let bodyInit: BodyInit | null = null;
  if (payload !== undefined && mutating) {
    headers['Content-Type'] = 'application/json';
    bodyInit = JSON.stringify(payload);
  }

  const response = await coreFetch(method, path, {
    body: bodyInit,
    params,
    options: { ...options, headers },
    mutating,
    // coreFetch auto-generates only when no key was supplied above.
    idempotentPayment: idempotentPayment && paymentKey === null,
  });

  return readJson<T>(response);
}

async function requestUpload<T>(path: string, form: FormData, options?: RequestOptions): Promise<T> {
  const response = await coreFetch('POST', path, {
    body: form,
    options,
    mutating: true,
  });
  return readJson<T>(response);
}

async function download(path: string, params?: QueryParams, options?: RequestOptions): Promise<Blob> {
  const response = await coreFetch('GET', path, { params, options });
  return response.blob();
}

export const api = {
  get: <T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<T> =>
    requestJson<T>('GET', path, undefined, params, options),

  post: <T>(path: string, body?: unknown, params?: QueryParams, options?: RequestOptions): Promise<T> =>
    requestJson<T>('POST', path, body, params, options),

  put: <T>(path: string, body?: unknown, params?: QueryParams, options?: RequestOptions): Promise<T> =>
    requestJson<T>('PUT', path, body, params, options),

  patch: <T>(path: string, body?: unknown, params?: QueryParams, options?: RequestOptions): Promise<T> =>
    requestJson<T>('PATCH', path, body, params, options),

  del: <T>(path: string, body?: unknown, params?: QueryParams, options?: RequestOptions): Promise<T> =>
    requestJson<T>('DELETE', path, body, params, options),

  /** Multipart upload; the browser sets the content type boundary itself. */
  upload: <T>(path: string, form: FormData, options?: RequestOptions): Promise<T> =>
    requestUpload<T>(path, form, options),

  /** Fetch a binary document (CSV report export, attachment download). */
  download: (path: string, params?: QueryParams, options?: RequestOptions): Promise<Blob> =>
    download(path, params, options),
};

export type ApiClient = typeof api;
